import React, { useEffect, useState } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError } from "../lib/api";
import { fmtCLP, fmtDate, todayISO } from "../lib/format";
import { toast } from "sonner";
import { Wand2, Link2, Unlink } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { LibroPagination, useClientPagination } from "../components/LibroPagination";

export default function Conciliacion() {
  const [cuentas, setCuentas] = useState([]);
  const [cuentaId, setCuentaId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState(todayISO());
  const [selMov, setSelMov] = useState(null); // movimiento_bancario_id
  const [selLinea, setSelLinea] = useState(null); // {asiento_id, linea_idx}
  const [autoLoading, setAutoLoading] = useState(false);
  const [informeLoading, setInformeLoading] = useState(false);
  const [informe, setInforme] = useState(null);

  const load = async () => {
    if (!cuentaId) return;
    setLoading(true);
    try {
      const params = {};
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      const { data } = await api.get(`/conciliacion/${cuentaId}`, { params });
      setData(data);
    } catch (e) { toast.error(extractError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/cuentas-bancarias").then(({ data }) => {
      setCuentas(data);
      if (data.length > 0 && !cuentaId) setCuentaId(data[0].id);
    }).catch((e) => toast.error(extractError(e)));
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [cuentaId, desde, hasta]);

  const bancoMovs = data?.movimientos_banco || [];
  const libroLineas = data?.lineas_libro || [];
  const bancoPage = useClientPagination(bancoMovs, `${cuentaId}|${desde}|${hasta}|banco|${bancoMovs.length}`);
  const libroPage = useClientPagination(libroLineas, `${cuentaId}|${desde}|${hasta}|libro|${libroLineas.length}`);

  const match = async () => {
    if (!selMov || !selLinea) return;
    try {
      await api.post("/conciliacion/match", {
        movimiento_id: selMov,
        asiento_id: selLinea.asiento_id,
        linea_idx: selLinea.linea_idx,
      });
      toast.success("Conciliado");
      setSelMov(null); setSelLinea(null);
      load();
    } catch (err) { toast.error(extractError(err)); }
  };

  const unmatch = async (mid) => {
    try { await api.post(`/conciliacion/unmatch/${mid}`); toast.success("Desconciliado"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };

  const autoMatch = async () => {
    setAutoLoading(true);
    try {
      const { data } = await api.post(`/conciliacion/auto/${cuentaId}`);
      toast.success(`Auto-conciliados ${data.matched} · Pendientes ${data.pendientes_movs}`);
      load();
    } catch (err) { toast.error(extractError(err)); }
    finally { setAutoLoading(false); }
  };

  const verInforme = async () => {
    if (!cuentaId) return;
    setInformeLoading(true);
    try {
      const params = {};
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      const { data } = await api.get(`/conciliacion/${cuentaId}/informe`, { params });
      setInforme(data);
      const r = data.resumen || {};
      toast.success(
        r.cuadrado
          ? "Informe: conciliación cuadrada"
          : `Informe: ${r.movs_pendientes || 0} movs y ${r.lineas_pendientes || 0} líneas pendientes`
      );
    } catch (e) { toast.error(extractError(e)); }
    finally { setInformeLoading(false); }
  };

  const canMatch = selMov && selLinea && (() => {
    const m = data?.movimientos_banco.find((x) => x.id === selMov);
    const l = data?.lineas_libro.find((x) => x.asiento_id === selLinea.asiento_id && x.linea_idx === selLinea.linea_idx);
    if (!m || !l) return false;
    const target = m.abono - m.cargo;
    return Math.abs(target - l.monto_neto) < 0.5;
  })();

  return (
    <Page>
      <PageHeader
        title="Conciliación bancaria"
        subtitle="Coteja cartola con asientos registrados del libro (los borradores no participan)"
        testid="conc-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={cuentaId} onValueChange={setCuentaId}>
              <SelectTrigger className="text-sm h-9 w-56"><SelectValue placeholder="Seleccionar cuenta..." /></SelectTrigger>
              <SelectContent>{cuentas.map((c) => <SelectItem key={c.id} value={c.id}>{c.banco} · {c.numero}</SelectItem>)}</SelectContent>
            </Select>
            <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="px-2 py-1.5 border border-border rounded-sm text-sm" />
            <span className="text-xs text-muted-foreground">→</span>
            <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="px-2 py-1.5 border border-border rounded-sm text-sm" />
            <button onClick={autoMatch} disabled={autoLoading || !cuentaId} data-testid="conc-auto"
              className="inline-flex items-center gap-1.5 border border-warning-foreground text-warning-foreground hover:bg-warning/15 disabled:opacity-50 text-sm px-3 py-1.5 rounded-sm">
              <Wand2 className="h-4 w-4" /> {autoLoading ? "Conciliando…" : "Auto-conciliar"}
            </button>
            <button onClick={verInforme} disabled={!cuentaId || informeLoading} data-testid="conc-informe"
              className="inline-flex items-center gap-1.5 border border-border hover:bg-muted disabled:opacity-50 text-sm px-3 py-1.5 rounded-sm">
              {informeLoading ? "Informe…" : "Informe"}
            </button>
          </div>
        }
      />

      {data && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
          <KPI label="Saldo banco" value={fmtCLP(data.totales.saldo_banco)} tone="primary" />
          <KPI label="Saldo libro" value={fmtCLP(data.totales.saldo_libro)} />
          <KPI label="Diferencia" value={fmtCLP(data.totales.diferencia, { showSign: true })}
            tone={Math.abs(data.totales.diferencia) < 0.5 ? "primary" : "danger"} />
          <KPI label="Movs pendientes" value={data.totales.movs_pendientes} tone="warning" />
          <KPI label="Líneas pendientes" value={data.totales.lineas_pendientes} tone="warning" />
        </div>
      )}

      {informe && (
        <div className="mb-4 bg-card border border-border rounded-md p-4" data-testid="conc-informe-panel">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div>
              <div className="label-caps">Informe de conciliación</div>
              <div className="text-[12px] text-muted-foreground">
                {informe.cuenta?.banco} · {informe.cuenta?.numero}
                {informe.desde || informe.hasta ? ` · ${informe.desde || "…"} → ${informe.hasta || "…"}` : ""}
              </div>
            </div>
            <button onClick={() => setInforme(null)} className="text-xs text-muted-foreground hover:text-foreground">Cerrar</button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
            <div>Diferencia: <span className="num font-semibold">{fmtCLP(informe.resumen?.diferencia, { showSign: true })}</span></div>
            <div>Movs pend.: <span className="font-semibold">{informe.resumen?.movs_pendientes}</span></div>
            <div>Líneas pend.: <span className="font-semibold">{informe.resumen?.lineas_pendientes}</span></div>
            <div>{informe.resumen?.cuadrado ? "✓ Cuadrada" : "Pendiente de cuadrar"}</div>
          </div>
          <div className="text-[11px] text-muted-foreground mt-2">{informe.nota}</div>
        </div>
      )}

      {selMov && selLinea && (
        <div className={`mb-4 rounded-md p-3 flex items-center justify-between gap-3 ${canMatch ? "bg-success/10 border border-success" : "bg-destructive/10 border border-destructive"}`}>
          <span className="text-sm">
            {canMatch ? "✓ Los montos coinciden. Listo para conciliar." : "✗ Los montos no coinciden — selecciona otra línea."}
          </span>
          <div className="flex gap-2">
            <button onClick={() => { setSelMov(null); setSelLinea(null); }} className="text-sm px-3 py-1 rounded-sm border border-border hover:bg-card">Cancelar</button>
            <button onClick={match} disabled={!canMatch} data-testid="conc-match"
              className="inline-flex items-center gap-1.5 bg-primary hover:bg-success disabled:opacity-40 text-primary-foreground text-sm px-3 py-1 rounded-sm">
              <Link2 className="h-4 w-4" /> Conciliar
            </button>
          </div>
        </div>
      )}

      {loading ? <div className="p-8 text-center text-muted-foreground text-sm">Cargando…</div>
        : !data ? <div className="p-8 text-center text-muted-foreground text-sm">Selecciona una cuenta bancaria.</div>
        : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {/* Banco */}
          <div className="bg-card border border-border rounded-md overflow-x-auto">
            <div className="px-4 py-2 border-b border-border bg-background flex justify-between items-center">
              <span className="label-caps">Cartola del banco ({data.movimientos_banco.length})</span>
            </div>
            <table className="acc-table" data-testid="conc-banco">
              <thead><tr><th>Fecha</th><th>Descripción</th><th className="num">Cargo</th><th className="num">Abono</th><th></th></tr></thead>
              <tbody>
                {bancoPage.pageItems.map((m) => (
                  <tr key={m.id} onClick={() => !m.conciliado && setSelMov(m.id === selMov ? null : m.id)}
                    className={`cursor-pointer ${selMov === m.id ? "bg-primary/10" : ""} ${m.conciliado ? "opacity-60" : ""}`}>
                    <td>{fmtDate(m.fecha)}</td>
                    <td className="text-[12px]">{m.descripcion || "—"}</td>
                    <td className="num text-destructive">{m.cargo ? fmtCLP(m.cargo) : ""}</td>
                    <td className="num text-primary">{m.abono ? fmtCLP(m.abono) : ""}</td>
                    <td>
                      {m.conciliado ? (
                        <button onClick={(e) => { e.stopPropagation(); unmatch(m.id); }} title="Desconciliar"
                          className="text-destructive hover:bg-destructive/10 p-1 rounded-sm"><Unlink className="h-3.5 w-3.5" /></button>
                      ) : <span className="text-[10px] text-muted-foreground">●</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <LibroPagination
              pagination={bancoPage.pagination}
              pageSize={bancoPage.pageSize}
              onPageSizeChange={bancoPage.setPageSize}
              onPageChange={bancoPage.setPage}
              testidPrefix="conc-banco"
            />
          </div>

          {/* Libro */}
          <div className="bg-card border border-border rounded-md overflow-x-auto">
            <div className="px-4 py-2 border-b border-border bg-background flex justify-between items-center">
              <span className="label-caps">Libro contable ({data.lineas_libro.length})</span>
            </div>
            <table className="acc-table" data-testid="conc-libro">
              <thead><tr><th>Fecha</th><th>Asiento</th><th>Glosa</th><th className="num">Monto</th></tr></thead>
              <tbody>
                {libroPage.pageItems.map((l) => (
                  <tr key={l.id}
                    onClick={() => !l.conciliado && setSelLinea(selLinea?.asiento_id === l.asiento_id && selLinea.linea_idx === l.linea_idx ? null : { asiento_id: l.asiento_id, linea_idx: l.linea_idx })}
                    className={`cursor-pointer ${selLinea?.asiento_id === l.asiento_id && selLinea?.linea_idx === l.linea_idx ? "bg-primary/10" : ""} ${l.conciliado ? "opacity-60" : ""}`}>
                    <td>{fmtDate(l.fecha)}</td>
                    <td className="font-mono text-[11px]">#{l.numero}</td>
                    <td className="text-[12px]">{l.glosa}</td>
                    <td className={`num font-semibold ${l.monto_neto >= 0 ? "text-primary" : "text-destructive"}`}>
                      {fmtCLP(l.monto_neto, { showSign: true })}
                      {l.conciliado && <span className="ml-1 text-[10px] text-primary">✓</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <LibroPagination
              pagination={libroPage.pagination}
              pageSize={libroPage.pageSize}
              onPageSizeChange={libroPage.setPageSize}
              onPageChange={libroPage.setPage}
              testidPrefix="conc-libro"
            />
          </div>
        </div>
      )}
    </Page>
  );
}

function KPI({ label, value, tone = "default" }) {
  const tones = { default: "text-foreground", primary: "text-primary", danger: "text-destructive", warning: "text-warning-foreground" };
  return (
    <div className="bg-card border border-border rounded-md p-3 sm:p-4">
      <div className="label-caps mb-1">{label}</div>
      <div className={`num text-lg font-semibold ${tones[tone]}`}>{value}</div>
    </div>
  );
}
